/**
 * Página principal de gestión de usuarios
 * Sistema Klever Octopus - Betel Natural
 */
import React, { useState } from 'react';
import {
  FiPlus, FiSearch, FiX, FiAlertTriangle, FiArrowUp,
  FiEye, FiEdit, FiKey, FiTrash2, FiUsers, FiChevronLeft, FiChevronRight
} from 'react-icons/fi';
import {
  useUsers,
  useAllRoles,
  useUserStats
} from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';
import { StatsCard } from '../../components/UI/StatsCard';
import { Modal, ConfirmModal } from '../../components/UI/Modal';
import { Loading } from '../../components/UI/Loading';
import UserForm from './UserForm';
import UserDetailModal from './UserDetailModal';
import UserPasswordModal from './UserPasswordModal';

const Users = () => {
  const { user: currentUser, hasPermission } = useAuth();
  
  // Estados locales
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'

  // Hooks para datos
  const {
    users,
    loading,
    error,
    pagination,
    filters,
    createUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    updateFilters,
    changePage,
    changeItemsPerPage,
    clearFilters,
    setError
  } = useUsers();

  const { roles } = useAllRoles();
  const { stats, loading: statsLoading } = useUserStats();

  // Verificar permisos
  const canCreate = hasPermission('manage_users') || hasPermission('create_user');
  const canEdit = hasPermission('manage_users') || hasPermission('edit_user');
  const canDelete = hasPermission('manage_users') || hasPermission('delete_user');
  const canChangePassword = hasPermission('manage_users') || hasPermission('change_user_password');

  // Manejadores de eventos
  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalMode('create');
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setModalMode('edit');
    setShowUserModal(true);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowDetailModal(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setShowPasswordModal(true);
  };

  const handleToggleStatus = async (user) => {
    const newStatus = !user.is_active;
    const result = await toggleUserStatus(user.id, newStatus);
    
    if (!result.success) {
      alert(`Error: ${result.message}`);
    }
  };

  const handleFormSubmit = async (userData) => {
    let result;
    
    if (modalMode === 'create') {
      result = await createUser(userData);
    } else {
      result = await updateUser(selectedUser.id, userData);
    }

    if (result.success) {
      setShowUserModal(false);
      setSelectedUser(null);
    } else {
      // Los errores se manejan en el hook
      return result;
    }
  };

  const confirmDelete = async () => {
    const result = await deleteUser(selectedUser.id);
    
    if (result.success) {
      setShowDeleteModal(false);
      setSelectedUser(null);
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleSearch = (searchTerm) => {
    updateFilters({ search: searchTerm });
  };

  const handleRoleFilter = (role) => {
    updateFilters({ role });
  };

  const handleStatusFilter = (status) => {
    updateFilters({ status });
  };

  const handleSort = (field) => {
    const newOrder = filters.sort_by === field && filters.sort_order === 'asc' ? 'desc' : 'asc';
    updateFilters({ sort_by: field, sort_order: newOrder });
  };

  if (loading && users.length === 0) {
    return <Loading message="Cargando usuarios..." />;
  }

  return (
    <div className="users-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">Gestión de Usuarios</h1>
          <p className="page-description">
            Administra usuarios, roles y permisos del sistema
          </p>
        </div>
        
        {canCreate && (
          <button 
            className="btn btn-primary"
            onClick={handleCreateUser}
          >
            <FiPlus />
            Nuevo Usuario
          </button>
        )}
      </div>

      {/* Estadísticas */}
      {!statsLoading && stats && (
        <div className="stats-container">
          <div className="stats-grid">
            <StatsCard
              title="Total Usuarios"
              value={stats.total || 0}
              variant="primary"
            />
            <StatsCard
              title="Usuarios Activos"
              value={stats.active || 0}
              variant="success"
            />
            <StatsCard
              title="Usuarios Inactivos"
              value={stats.inactive || 0}
              variant="warning"
            />
            <StatsCard
              title="Roles Configurados"
              value={roles.length}
              variant="info"
            />
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="filters-section">
        <div className="filters-row">
          {/* Búsqueda */}
          <div className="search-field">
            <FiSearch />
            <input
              type="text"
              placeholder="Buscar por nombre, email..."
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              className="form-control"
            />
          </div>

          {/* Filtro por rol */}
          <select
            value={filters.role}
            onChange={(e) => handleRoleFilter(e.target.value)}
            className="form-control"
          >
            <option value="">Todos los roles</option>
            {roles.map((role, index) => (
              <option key={role.id || role.name || index} value={role.name || role}>
                {role.display_name || role.name || role}
              </option>
            ))}
          </select>

          {/* Filtro por estado */}
          <select
            value={filters.status}
            onChange={(e) => handleStatusFilter(e.target.value)}
            className="form-control"
          >
            <option value="">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>

          {/* Limpiar filtros */}
          <button 
            className="btn btn-outline-secondary"
            onClick={clearFilters}
          >
            <FiX />
            Limpiar
          </button>
        </div>
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="alert alert-error">
          <FiAlertTriangle />
          {error}
          <button 
            className="btn btn-link"
            onClick={() => setError(null)}
          >
            <FiX />
          </button>
        </div>
      )}

      {/* Tabla de usuarios */}
      <div className="table-container">
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th 
                  className={`sortable ${filters.sort_by === 'nombre' ? filters.sort_order : ''}`}
                  onClick={() => handleSort('nombre')}
                >
                  Usuario
                  <FiArrowUp />
                </th>
                <th 
                  className={`sortable ${filters.sort_by === 'email' ? filters.sort_order : ''}`}
                  onClick={() => handleSort('email')}
                >
                  Email
                  <FiArrowUp />
                </th>
                <th>Rol</th>
                <th 
                  className={`sortable ${filters.sort_by === 'is_active' ? filters.sort_order : ''}`}
                  onClick={() => handleSort('is_active')}
                >
                  Estado
                  <FiArrowUp />
                </th>
                <th 
                  className={`sortable ${filters.sort_by === 'created_at' ? filters.sort_order : ''}`}
                  onClick={() => handleSort('created_at')}
                >
                  Fecha Creación
                  <FiArrowUp />
                </th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        {(user.nombre?.charAt(0) || 'U').toUpperCase()}
                      </div>
                      <div className="user-details">
                        <span className="user-name">
                          {user.nombre} {user.apellido}
                        </span>
                        <small className="user-id">ID: {user.id}</small>
                      </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span 
                      className={`role-badge role-${user.role?.name || 'default'}`}
                      style={{ backgroundColor: user.role?.color }}
                    >
                      {user.role?.display_name || user.role?.name || 'Sin rol'}
                    </span>
                  </td>
                  <td>
                    <span 
                      className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}
                    >
                      <i className={`fas fa-circle ${user.is_active ? 'text-success' : 'text-danger'}`}></i>
                      {user.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleViewUser(user)}
                        title="Ver detalles"
                      >
                        <FiEye />
                      </button>
                      
                      {canEdit && (
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleEditUser(user)}
                          title="Editar"
                        >
                          <FiEdit />
                        </button>
                      )}
                      
                      {canChangePassword && (
                        <button
                          className="btn btn-sm btn-outline-warning"
                          onClick={() => handleChangePassword(user)}
                          title="Cambiar contraseña"
                        >
                          <FiKey />
                        </button>
                      )}
                      
                      {canEdit && (
                        <button
                          className={`btn btn-sm ${user.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                          onClick={() => handleToggleStatus(user)}
                          title={user.is_active ? 'Desactivar' : 'Activar'}
                        >
                          <i className={`fas ${user.is_active ? 'fa-user-times' : 'fa-user-check'}`}></i>
                        </button>
                      )}
                      
                      {canDelete && user.id !== currentUser?.id && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteUser(user)}
                          title="Eliminar"
                        >
                          <FiTrash2 />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {users.length === 0 && !loading && (
            <div className="empty-state">
              <FiUsers />
              <h3>No se encontraron usuarios</h3>
              <p>No hay usuarios que coincidan con los filtros aplicados.</p>
              {canCreate && (
                <button 
                  className="btn btn-primary"
                  onClick={handleCreateUser}
                >
                  Crear primer usuario
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Paginación */}
      {pagination.totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Mostrando {users.length} de {pagination.totalItems} usuarios
          </div>
          
          <div className="pagination-controls">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => changePage(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
            >
              <FiChevronLeft />
              Anterior
            </button>

            <span className="pagination-info">
              Página {pagination.currentPage} de {pagination.totalPages}
            </span>

            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => changePage(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              Siguiente
              <FiChevronRight />
            </button>
          </div>

          <select
            value={pagination.itemsPerPage}
            onChange={(e) => changeItemsPerPage(parseInt(e.target.value))}
            className="form-control pagination-select"
          >
            <option value={10}>10 por página</option>
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </select>
        </div>
      )}

      {/* Modales */}
      {showUserModal && (
        <Modal
          title={modalMode === 'create' ? 'Crear Usuario' : 'Editar Usuario'}
          isOpen={showUserModal}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
          size="large"
        >
          <UserForm
            user={selectedUser}
            mode={modalMode}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setShowUserModal(false);
              setSelectedUser(null);
            }}
          />
        </Modal>
      )}

      {showDetailModal && selectedUser && (
        <UserDetailModal
          user={selectedUser}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedUser(null);
          }}
          onEdit={canEdit ? () => {
            setShowDetailModal(false);
            handleEditUser(selectedUser);
          } : null}
        />
      )}

      {showPasswordModal && selectedUser && (
        <UserPasswordModal
          user={selectedUser}
          isOpen={showPasswordModal}
          onClose={() => {
            setShowPasswordModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowPasswordModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {showDeleteModal && selectedUser && (
        <ConfirmModal
          isOpen={showDeleteModal}
          title="Confirmar eliminación"
          message={`¿Estás seguro de que deseas eliminar al usuario "${selectedUser.nombre} ${selectedUser.apellido}"?`}
          confirmText="Eliminar"
          confirmClass="btn-danger"
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {loading && (
        <div className="loading-overlay">
          <Loading message="Procesando..." />
        </div>
      )}
    </div>
  );
};

export default Users;
